"use client";

import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import type { SpotAccuracyMetric } from "@/lib/accuracy/summary";
import { isSupProfileKey, sportPresentation } from "@/lib/conditions/sport-profiles";

type AccuracySpotTableProps = {
  spots: SpotAccuracyMetric[];
};

export function AccuracySpotTable({ spots }: AccuracySpotTableProps) {
  const [query, setQuery] = useState("");
  const [sport, setSport] = useState("all");

  const sports = useMemo(
    () => ["all", ...Array.from(new Set(spots.map((spot) => spot.sport))).sort()],
    [spots],
  );
  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return spots.filter((spotRow) => {
      const matchesSport = sport === "all" || spotRow.sport === sport;
      const matchesSearch =
        search.length === 0 ||
        spotRow.spotName.toLowerCase().includes(search) ||
        spotRow.spotSlug.toLowerCase().includes(search);
      return matchesSport && matchesSearch;
    });
  }, [query, sport, spots]);

  return (
    <section className="bg-white px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 border-b border-border pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="font-serif text-[30px] leading-tight text-navy sm:text-[38px]">
              Spot revision ledger
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-text-muted">
              Search spots with enough completed day-ahead comparisons against
              later output from the same weather provider.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-[minmax(220px,320px)_180px]">
            <label className="relative block">
              <span className="sr-only">Search spots</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search spots"
                className="min-h-[44px] w-full rounded-sm border border-border bg-warm-bg py-2 pl-9 pr-3 text-sm font-semibold text-navy outline-none focus:border-teal focus:ring-2 focus:ring-teal/20"
              />
            </label>
            <label className="block">
              <span className="sr-only">Filter by sport</span>
              <select
                value={sport}
                onChange={(event) => setSport(event.target.value)}
                className="min-h-[44px] w-full rounded-sm border border-border bg-warm-bg px-3 text-sm font-semibold text-navy outline-none focus:border-teal focus:ring-2 focus:ring-teal/20"
              >
                {sports.map((sportOption) => (
                  <option key={sportOption} value={sportOption}>
                    {sportOption === "all" ? "All sports" : sportLabel(sportOption)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 text-left text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-[1px] text-text-muted">
                <th className="border-b border-border py-3 pr-4 font-semibold">Spot</th>
                <th className="border-b border-border px-4 py-3 font-semibold">Sport</th>
                <th className="border-b border-border px-4 py-3 font-semibold">Rows</th>
                <th className="border-b border-border px-4 py-3 font-semibold">Stable rate</th>
                <th className="border-b border-border px-4 py-3 font-semibold">Revisions</th>
                <th className="border-b border-border py-3 pl-4 font-semibold">Mean revision</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 80).map((spotRow) => (
                <tr key={`${spotRow.spotSlug}-${spotRow.sport}`} className="text-charcoal">
                  <td className="border-b border-border-light py-4 pr-4 font-semibold text-navy">
                    {spotRow.spotName}
                  </td>
                  <td className="border-b border-border-light px-4 py-4">
                    {sportLabel(spotRow.sport)}
                  </td>
                  <td className="border-b border-border-light px-4 py-4">
                    {spotRow.totalRows}
                  </td>
                  <td className="border-b border-border-light px-4 py-4">
                    {spotRow.hitRate}%
                  </td>
                  <td className="border-b border-border-light px-4 py-4 font-semibold text-terracotta">
                    {spotRow.missCount}
                  </td>
                  <td className="border-b border-border-light py-4 pl-4">
                    {formatError(spotRow.meanAbsoluteError)}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-sm text-text-muted">
                    No matching spot rows yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function formatError(value: number | null): string {
  return value === null ? "Not enough rows" : `${value.toFixed(2)} pts`;
}

function sportLabel(sport: string): string {
  if (isSupProfileKey(sport)) return `${sportPresentation(sport).label} (Beta)`;
  const labels: Record<string, string> = {
    piste: "Piste",
    "off-piste": "Off-piste",
    touring: "Touring",
    mtb: "MTB",
    surf: "Surf",
    wind: "Wind",
    foil: "Prone foil",
    "downwind-foil": "Downwind foil",
    "wing-foil": "Wing foil",
    bodyboarding: "Bodyboarding",
    diving: "Diving",
    sup: "SUP",
    swim: "Open water",
    "sailing-club": "Club sailing",
    "sailing-hp": "High-performance sailing",
    ski: "Ski",
  };
  return labels[sport] ?? sport;
}
